import React from 'react';
import PropTypes from 'prop-types';

import * as Sentry from '@sentry/browser';

import ThroneSeatElements, { SeatMapLegend } from './throne-seat-elements';

import SeatMapContainer from '@weezevent/throne';
import { SeatMapBackButton } from './seatmap-back-button';
import HoverSeat from '../../components/placement/hoverSeat';
import SelectedSeats from '../../components/placement/selectedSeats';

import useAppContext from '../../context/useAppContext';
import useSeatedContext from '../../context/seatedContext';
import { ToastContext } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';

import { fetcher } from '@weezevent/weezjs-core';
import { useMousePosition } from '../../utils/hooks';
import '../../styles/availability/seats.css';

export default function Seats({ availability, contingents, rates = [], onBack = () => {}, onContingentChanged = () => {} }) {
    const [hoveredSeat, setHoveredSeat] = React.useState(null);
    const [selectedSeats, setSelectedSeats] = React.useState([]);
    const [{ inventory }] = useSeatedContext();
    const [{ organization, event }] = useAppContext();
    const toast = React.useContext(ToastContext);
    const { t } = useTranslation();

    const mousePosition = useMousePosition();
    // Prepare seats
    availability.seats.forEach(seat => {
        let { booking_state } = seat;
        seat.state = booking_state.toLowerCase();
    });

    let filteredContingents = contingents
        ? contingents.filter(contingent => {
              let contingentRates = rates.filter(rate => {
                  return rate.resolved_contingents.indexOf(contingent.uuid > -1);
              });
              // At least one rate has the current inventory id
              return contingentRates.length && contingentRates.some(rate => rate.inventories.indexOf(inventory.uuid) > -1);
          })
        : [];

    const updateContingent = React.useCallback(
        (contingent, seats) => {
            let url = `/ticket/organizations/${organization.id}/events/${event.id}/inventories/${inventory.uuid}/seats/set_contingents`;
            fetcher
                .post(url, {
                    data: {
                        seats: seats.map(seat => seat.uuid),
                        contingent_uuid: contingent ? contingent.pk : null
                    }
                })
                .then(({ status, response }) => {
                    if (status !== 200) {
                        console.error('[Seats][Change contingent]', response);
                        throw new Error('Could not change contingent for seats');
                    }

                    // todo reload this page
                    onContingentChanged();
                    toast.success(t('ticket.avaailability.contingent_update_success'));
                })
                .catch(e => {
                    // Catch sentry
                    Sentry.captureException(e);
                    toast.error(t('ticket.avaailability.contingent_update_error'));
                });
        },
        [organization, event, inventory, onContingentChanged]
    );

    return (
        <div>
            <SeatMapBackButton name={availability.name} onBack={onBack} />
            {selectedSeats?.length ? (
                <SelectedSeats
                    assignContingent={updateContingent}
                    contingents={filteredContingents}
                    currentInventory={inventory}
                    mouseX={mousePosition.x}
                    mouseY={mousePosition.y}
                    seats={selectedSeats}
                    rate={rates}
                    unselectSeats={() => setSelectedSeats(null)}
                />
            ) : null}
            <SeatMapContainer
                showScene={false}
                showPopup={!selectedSeats?.length}
                minimap={false}
                zone={availability}
                onSeatOver={seat => {
                    if (selectedSeats?.length) {
                        return;
                    }

                    setHoveredSeat(seat);
                }}
                onSeatsSelected={setSelectedSeats}
                onSeatClicked={s => setSelectedSeats([s])}
                mapOptions={{
                    showRowNumbers: true,
                    zoomControls: false,
                    offset: false,
                    seatElements: ThroneSeatElements
                }}
                popover={<HoverSeat zone={hoveredSeat} contingents={filteredContingents} />}
            />
            <SeatMapLegend seats={availability.seats} />
        </div>
    );
}

Seats.propTypes = {
    availability: PropTypes.object.isRequired,
    contingents: PropTypes.array.isRequired,
    rates: PropTypes.array.isRequired,
    onBack: PropTypes.func,
    onContingentChanged: PropTypes.func
};
